import { GlobalVariable } from '@kemu-io/kemu-core/common/managers/globalVariables.js';
import { serializeGlobalVariables } from '@src/app/recipe/utils';

/**
 * Exports global variables to JSON string
 * @param variables - The global variables to export
 * @param includeValues - Whether to include lastValue property. If true, uses serializeGlobalVariables to convert binary data to base64
 * @returns JSON string with format { globalVariables: GlobalVariable[] }
 */
export const exportGlobalVariables = (variables: GlobalVariable[], includeValues: boolean): string => {
  let variablesToExport: GlobalVariable[];
  
  if (includeValues) {
    // Use serializeGlobalVariables to convert binary data to base64
    variablesToExport = serializeGlobalVariables(variables);
  } else {
    // Remove lastValue property from each variable
    variablesToExport = variables.map(({ lastValue, ...rest }) => ({
      ...rest,
      lastValue: null,
    }));
  }

  const exportData = {
    globalVariables: variablesToExport,
  };

  return JSON.stringify(exportData, null, 2);
};

/**
 * Copies text to clipboard
 * @param text - The text to copy
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    throw error;
  }
};

/**
 * Downloads a file with the given content and filename
 * @param content - The file content as string
 * @param filename - The filename to use
 */
export const downloadFile = (content: string, filename: string): void => {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Reads text from clipboard
 * @returns The clipboard text or null if unavailable
 */
export const readClipboard = async (): Promise<string | null> => {
  try {
    const text = await navigator.clipboard.readText();
    return text;
  } catch (error) {
    console.error('Failed to read clipboard:', error);
    return null;
  }
};

/**
 * Validates that the JSON string contains a valid globalVariables configuration
 * @param json - The JSON string to validate
 * @returns Object with valid flag and optional data array
 */
export const validateGlobalVariablesJson = (json: string): { valid: boolean; data?: GlobalVariable[] } => {
  try {
    const parsed = JSON.parse(json);
    
    if (!parsed || typeof parsed !== 'object') {
      return { valid: false };
    }

    if (!parsed.globalVariables || !Array.isArray(parsed.globalVariables)) {
      return { valid: false };
    }

    // Basic validation that each item has required properties
    const isValid = parsed.globalVariables.every((item: any) => {
      return item && 
             typeof item.name === 'string' && 
             typeof item.controlType === 'string';
    });

    if (!isValid) {
      return { valid: false };
    }

    return {
      valid: true,
      data: parsed.globalVariables as GlobalVariable[],
    };
  } catch (error) {
    return { valid: false };
  }
};

/**
 * Reads a file as text
 * @param file - The file to read
 * @returns The file content as string
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to read file as text'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Generates a filename with timestamp for global variables export
 * @returns Filename in format 'kemu-global-vars-{timestamp}.json'
 */
export const generateExportFilename = (): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  return `kemu-global-vars-${timestamp}.json`;
};

